/**
 * The day, in pixels.
 *
 * A timeline item is an offset and a length, and there is no sort column —
 * order *is* time. So a block's vertical position cannot be a rank that
 * happens to look chronological; it has to be the time itself, or dragging a
 * block would have to invent an offset the planner never asked for. Every
 * number the axis renders and every number a drag produces comes from here.
 *
 * Pure on purpose: no DOM, no React, no clock. Given the same items and the
 * same hours this answers the same way in a test as it does under a pointer,
 * which is what makes the arithmetic checkable without simulating a drag.
 *
 * Everything is in *offset* minutes — signed minutes from `weddings.startTime`,
 * the same units the rows are stored in. Wall-clock never enters; `startTime`
 * and `endTime` are here only to say how far away the end of the night is.
 */
import {
  MAX_OFFSET_MINUTES,
  elapsedMinutes,
  minutesSinceMidnight,
  parseTimeOfDay,
} from '@vendorops/shared';

/**
 * Two pixels a minute: a 30-minute item is a 60px block, which is enough to
 * carry a label.
 *
 * A whole calendar day is 2880px at that rate, which is more than a card can
 * show at once — so what keeps the day from becoming a wall a planner gets lost
 * in is not the scale, it is `MAX_AXIS` in TimelineAxis.tsx. The axis gets its
 * own scroller and the page keeps its own scrollbar.
 */
export const PX_PER_MINUTE = 2;

/**
 * Five minutes. The default ladder is all multiples of 15, so ordinary edits
 * land clean, and 6:20 is still one fine-grained drag away.
 */
export const SNAP_MINUTES = 5;

/**
 * The shortest a block is ever drawn.
 *
 * A zero-length item — the send-off at the exact moment the night ends — is a
 * real thing a planner schedules, and at its true height it would be invisible
 * and, worse, impossible to grab a second time. One snap step is 10px, so the
 * floor sits just above it: a moment reads as a moment, and a real five-minute
 * item is overstated by two pixels rather than by a dozen.
 */
export const MIN_BLOCK_PX = 12;

/**
 * What layout needs of an item: where it starts, and how long it actually runs.
 *
 * `resolvedDurationMinutes` is the server's answer, not the stored field, and
 * **0 and null are different answers** — 0 is a point in time, null is
 * open-ended. Anything reading this with a truthiness check draws the ordinary
 * case as the broken one.
 */
export interface TimelineGeometryItem {
  offsetMinutes: number;
  resolvedDurationMinutes: number | null;
}

/**
 * What layout needs of a coverage window: when it opens and how long it runs.
 *
 * `durationMinutes` is a plain number with no null case, unlike a block's
 * `resolvedDurationMinutes`. A timeline item may store null because there is
 * always something downstream to borrow a length from — the next item, or the
 * end of the night if it is last. A coverage window has no such chain: it
 * always states its own end, so there is no open-ended capsule to draw to the
 * bottom of the axis and nothing circular to resolve. That is why the rail's
 * capsules can be built straight from these two numbers while the blocks need
 * `contentEndMinutes` computed first.
 */
export interface TimelineCoverageSpan {
  offsetMinutes: number;
  durationMinutes: number;
}

export interface TimelineCoverageBand<T> {
  annotation: T;
  top: number;
  height: number;
  /** Which column of the rail this capsule sits in, from 0. */
  lane: number;
  /**
   * How many columns its overlap group needs — every capsule in the group
   * reports the same number, so the rail can size itself from the widest group
   * by taking the maximum across all of them.
   */
  laneCount: number;
}

export interface TimelineBlock<T> {
  item: T;
  top: number;
  height: number;
  /** Which column of an overlap group, from 0. */
  lane: number;
  /** How many columns that group needs — every block in it reports the same. */
  laneCount: number;
  /**
   * The minutes the block is *drawn* as, which for an open-ended item is the
   * run to the end of the night rather than the null it stores. This is the
   * length to hand `edgeDrag`: the planner grabbed the edge they can see.
   */
  effectiveDurationMinutes: number;
}


/** Enough of a layout to convert between minutes and pixels. */
export interface TimelineScale {
  startOffsetMinutes: number;
}

/**
 * `C` defaults to `TimelineCoverageSpan` so that every annotation written
 * before coverage existed — `TimelineLayout<TimelineItemView>` in
 * TimelineAxis.tsx and its helpers — keeps meaning what it meant. A second
 * parameter without a default would have made adding coverage a rename across
 * every component that so much as names this type.
 */
export interface TimelineLayout<T, C = TimelineCoverageSpan> extends TimelineScale {
  blocks: TimelineBlock<T>[];
  /**
   * The capsules in the coverage rail beside the day, in the order the windows
   * open.
   *
   * Empty unless a caller passes coverage, which is what keeps this additive:
   * a consumer that does not know about coverage maps an empty array.
   */
  coverageBands: TimelineCoverageBand<C>[];
  /**
   * The bottom of the drawn axis, which is always a day boundary — midnight.
   *
   * Not where the scheduled day ends: that is `contentEndMinutes`, local to
   * `layout`, and it is the number an open-ended block is drawn to. One field
   * meant both while the axis stopped at the last thing on it; leaving it that
   * way once the axis reached midnight would have drawn a block storing
   * `durationMinutes: null` to midnight on a night that finished at 11:30 — half
   * an hour nobody scheduled, handed to `edgeDrag` as the length the planner
   * grabbed.
   */
  endOffsetMinutes: number;
  totalHeight: number;
  /** Where to draw the end-of-night line, or null when no end time is set. */
  endOfNightTop: number | null;
}


/**
 * Where an offset sits, in pixels from the top of the axis.
 *
 * Every consumer routes through here rather than multiplying for itself. It is
 * one line, so the saving is not the arithmetic — it is that the gutter, the
 * blocks, a drag and a click on empty day cannot end up disagreeing about what
 * a pixel is worth.
 */
export function topForOffset(scale: TimelineScale, offsetMinutes: number): number {
  return (offsetMinutes - scale.startOffsetMinutes) * PX_PER_MINUTE;
}

/** The inverse: what time a pixel on the axis is. */
export function offsetForTop(scale: TimelineScale, top: number): number {
  return scale.startOffsetMinutes + top / PX_PER_MINUTE;
}

/**
 * Where the scroller opens: event start, a quarter hour down from the top edge.
 *
 * Through `topForOffset` like every other consumer, for the same reason — an
 * anchor that multiplied for itself could put the view at a pixel the gutter
 * beside it labels as a different time.
 *
 * Clamped at zero because this is a scroll position, not a coordinate. The
 * browser would clamp it anyway; the floor is here so that a negative result
 * reads as arithmetic gone wrong rather than as a day too short to scroll.
 */
export function anchorScrollTop(scale: TimelineScale): number {
  return Math.max(0, topForOffset(scale, -EVENT_START_LEAD_MINUTES));
}

/**
 * A calendar day, in minutes.
 *
 * The axis is measured in whole ones of these. It used to be measured in a
 * margin — three hours before the earliest item and three after the last,
 * six before commit 6d1cd19 cut it — and the trouble with a margin is that it
 * is both arbitrary and mobile: a planner asks "where does the morning go",
 * not "how many hours of morning are there", and a margin measured from
 * whatever is scheduled first moves when they delete it.
 *
 * A date does not move, it is the unit `weddings.weddingDate` is already
 * stored in, and it is the unit the blocks already speak in — `TimelineBlock`
 * has always said "Next morning" rather than naming a day. Measuring the axis
 * in whole ones of these is what let the axis start naming them outright: its
 * day dividers fall on these boundaries, and `dayText`'s relative phrasing is
 * now the fallback for a wedding with no date set rather than the only option.
 */
export const DAY_MINUTES = 1440;

/**
 * How far into its own calendar date the wedding begins.
 *
 * Zero with no start time, which is the same answer `layout` has always given
 * in that case: there is no anchor, so there is no date, and the arithmetic
 * still has to come out somewhere. Every function below routes through this
 * rather than parsing for itself, so the day the selector names and the day
 * `layout` draws cannot disagree.
 */
function dayMinutesOf(startTime: string | null | undefined): number {
  const start = startTime ? parseTimeOfDay(startTime) : null;
  return start ? minutesSinceMidnight(start) : 0;
}

/**
 * The offset a day opens on — its own midnight, in the units the rows are
 * stored in.
 *
 * `dayIndex` counts calendar days from the wedding's own date, so 0 is the date
 * the couple married on and 1 is the morning after. It is view state and
 * nothing else: no column stores it, and this function and `dayIndexForOffset`
 * are the whole of the conversion between it and the offsets on disk.
 */
export function dayStartOffset(startTime: string | null | undefined, dayIndex: number): number {
  return dayIndex * DAY_MINUTES - dayMinutesOf(startTime);
}

/**
 * Which day an offset falls on.
 *
 * Floored rather than truncated, because the offsets either side of the
 * wedding's own midnight are negative and `Math.trunc` would put -1 and +1 on
 * the same day.
 */
export function dayIndexForOffset(
  startTime: string | null | undefined,
  offsetMinutes: number,
): number {
  return Math.floor((offsetMinutes + dayMinutesOf(startTime)) / DAY_MINUTES);
}

/**
 * The first and last day the selector may page to.
 *
 * The *schema's* bound, read through `MAX_OFFSET_MINUTES`, and deliberately not
 * the axis's own: a planner who could page past it would be looking at canvas
 * where a click creates an item `clampOffsetMinutes` pulls back into range,
 * which is the UI offering a time the route refuses. Against a 17:30 start it
 * comes to -2…+2.
 *
 * One case is degenerate and is left that way on purpose: a wedding starting at
 * exactly midnight has `+MAX_OFFSET_MINUTES` as the *first* minute of day +2,
 * so that day is pageable with a single schedulable minute in it. Asymmetric
 * arithmetic to exclude it would cost more clarity than the case is worth, and
 * everything else on such a day clamps — which is what every out-of-range
 * gesture on this axis already does.
 */
export function dayIndexBounds(startTime: string | null | undefined): [number, number] {
  return [
    dayIndexForOffset(startTime, -MAX_OFFSET_MINUTES),
    dayIndexForOffset(startTime, MAX_OFFSET_MINUTES),
  ];
}

/** `clampOffsetMinutes`'s twin, in days. */
export function clampDayIndex(startTime: string | null | undefined, dayIndex: number): number {
  const [first, last] = dayIndexBounds(startTime);
  return Math.min(last, Math.max(first, dayIndex));
}

/**
 * A clock time the planner typed, resolved against a day that is known.
 *
 * `offsetForClockTime` in `shared` cannot do this: a bare `2:00` names four
 * offsets inside the stored bound and nothing in it says which, so it settles
 * the question with a documented twelve-hour cutoff. That guess is right for a
 * form with no day in it and wrong for a block the planner can see — which is
 * on a day, and says which. There is nothing left to guess here, so the
 * arithmetic is exact and the round trip through `clockTimeForOffset` is
 * faithful on every day rather than on one.
 */
export function offsetForClockTimeOnDay(
  startTime: string | null | undefined,
  dayIndex: number,
  clockTime: string,
): number | null {
  const start = startTime ? parseTimeOfDay(startTime) : null;
  const target = parseTimeOfDay(clockTime);
  if (!start || !target) return null;
  return dayStartOffset(startTime, dayIndex) + minutesSinceMidnight(target);
}

/**
 * Whether a span belongs on the day drawn between two offsets.
 *
 * Half-open on the day — `[from, to)` — so a block that ends exactly at
 * midnight is the previous day's and one that starts there is this day's, and
 * neither is drawn twice.
 *
 * A *moment* is a special case rather than an oversight. It has no length to
 * run through a day with, so it is on the one day that contains it; read with
 * the general rule, `end > from` is false at the head of its own day and a
 * zero-length send-off would vanish from the axis altogether.
 */
function onDay(start: number, end: number, from: number, to: number): boolean {
  if (start >= to) return false;
  return start === end ? start >= from : end > from;
}

/**
 * Where a span is drawn, clamped to the day.
 *
 * A block or a capsule that crosses midnight is drawn on both of its days, cut
 * at the seam: a planner paging to the morning after a reception sees it still
 * running rather than an empty canvas, and the trailing block — which stores
 * `durationMinutes: null` and is therefore drawn to the end of the night — can
 * no longer stretch the canvas past the date it belongs to. The alternative,
 * giving a block to the day its start falls on and letting the canvas grow for
 * its tail, is the growth this axis exists to be rid of arriving by another
 * door.
 *
 * Floored at `MIN_BLOCK_PX` like every other box here: a sliver left in view at
 * a day's edge still has to be visible, and still has to be grabbable a second
 * time.
 *
 * Exported because a *live* box has to obey the same rule as a laid-out one.
 * The card under the pointer is placed from the gesture's own numbers rather
 * than from `layout`'s — see `dragPlacement` in TimelineAxis — and drawn
 * unclipped it runs off the foot of the day while being lengthened, taking its
 * own resize handle and its readout plate with it. Auto-scroll stops at the end
 * of the day, so the planner would be lengthening something they cannot see by
 * a handle that is no longer on screen.
 */
export function clippedBox(
  scale: TimelineScale,
  start: number,
  end: number,
  dayBottom: number,
): { top: number; height: number } {
  const top = Math.max(0, topForOffset(scale, start));
  return {
    top,
    height: Math.max(MIN_BLOCK_PX, Math.min(dayBottom, topForOffset(scale, end)) - top),
  };
}

/**
 * The whole axis: the blocks, and how tall the thing is.
 *
 * The domain is the wedding's own calendar date, midnight to midnight, grown
 * outward by whole days when anything falls outside it. No date is passed in and
 * none is needed: `startTime`'s minutes since midnight say how far into that
 * date the wedding begins, which is the only fact the arithmetic wants.
 *
 * Both edges are drawn whatever is scheduled in them, because clicking empty
 * canvas is how an item is created: a stretch the axis does not draw is a time a
 * planner cannot put anything at, and an axis that spanned only its own items
 * gave a wedding with nothing before the ceremony no morning to click on at
 * all.
 *
 * A date also does not move. The domain used to be a margin measured from the
 * earliest item, so deleting the morning's photographs moved the top of the
 * ruler and every pixel beneath it. Midnight stays where it is.
 *
 * Opening before the wedding is what keeps the morning's prep on screen — those
 * are negative offsets, and laid out against the start itself they would sit at
 * a negative top and clip off the card. An item running past the end of the
 * night is left where it falls, below the end-of-night line, because that
 * collision is exactly the thing the planner needs to see rather than the one
 * the layout quietly drops.
 */
/**
 * How much of the day before event start the view opens showing.
 *
 * The axis spans the wedding's whole calendar date, so it opens at midnight
 * unless something places it, and the thing worth placing it against is event
 * start — the minute the day turns public, guest arrival on the planner's own
 * ladder. (This constant predates the calendar-day axis, when what it opened
 * on was three hours of lead-in canvas rather than a whole morning. The reason
 * to anchor was the same then and the number is unchanged; only how much
 * unplaced day sits above it has grown.)
 *
 * A quarter hour rather than flush with the top edge, because the default
 * ladder schedules six real items *before* event start: anchored at exactly
 * offset 0 the timeline would open with three and a half hours of prep hidden
 * above the fold and nothing on screen admitting it. Fifteen minutes is 30px
 * against a 60px half-hour block, so the item above is cut in half — which
 * reads as a scroll, where a whole one in view would read as the anchor having
 * missed what it was aiming at.
 */
export const EVENT_START_LEAD_MINUTES = 15;

/**
 * Which column each span sits in, and how many columns its group needs.
 *
 * Lanes come from real overlap groups: a span joins the current group when it
 * starts *before* the group's running maximum end. Strictly before — a span
 * that merely touches the one above it, starting the minute it finishes, does
 * not overlap it, and half the default ladder is exactly that. Read touching as
 * overlapping and every adjacent pair splits into columns, which renders an
 * ordinary day as a pile-up.
 *
 * Shared by the blocks and the coverage rail, which is why it is a function
 * rather than the loop it used to be inside `layout`. The two have nothing else
 * in common — a block is an event in the lane area and a capsule is a vendor's
 * hours in a gutter of its own — but "how do things that overlap in time sit
 * beside each other" is one question, and answering it twice is how the rail
 * would come to disagree with the day about what touching means.
 *
 * Input must be sorted by `start`; `layout` sorts both of its lists already.
 * Every member of a group reports the same `laneCount`, so a caller can size a
 * column from any one of them.
 */
export function assignLanes(
  spans: readonly { start: number; end: number }[],
): { lane: number; laneCount: number }[] {
  const out: { lane: number; laneCount: number }[] = [];
  let group: number[] = [];
  let laneEnds: number[] = [];
  let groupEnd = -Infinity;

  const closeGroup = () => {
    for (const index of group) out[index]!.laneCount = laneEnds.length || 1;
    group = [];
    laneEnds = [];
    groupEnd = -Infinity;
  };

  for (const span of spans) {
    if (span.start >= groupEnd) closeGroup();
    // The first lane with nothing still running. Same rule, same boundary: a
    // lane whose last span ends exactly here is free.
    let lane = laneEnds.findIndex((end) => span.start >= end);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(span.end);
    } else {
      laneEnds[lane] = span.end;
    }
    groupEnd = Math.max(groupEnd, span.end);

    out.push({ lane, laneCount: 1 });
    group.push(out.length - 1);
  }
  closeGroup();

  return out;
}
export function layout<
  T extends TimelineGeometryItem,
  C extends TimelineCoverageSpan = TimelineCoverageSpan,
>(
  items: readonly T[],
  startTime: string | null | undefined,
  endTime: string | null | undefined,
  // Defaulted so that every existing call site keeps working untouched: the
  // axis, the drag tests and the panel all call this with three arguments, and
  // coverage arriving as a fourth must not be a change to any of them.
  coverage: readonly C[] = [],
  /**
   * Which calendar day to draw, counted from the wedding's own date — 0 is the
   * date the couple married on and 1 is the morning after.
   *
   * Defaulted so that every call site which only ever wanted the wedding day
   * keeps meaning what it meant, which before the date selector was all of
   * them.
   */
  dayIndex = 0,
): TimelineLayout<T, C> {
  // Minutes from the start to the end of the night, wrapping past midnight;
  // null when the planner has not said when it finishes.
  const span = elapsedMinutes(startTime, endTime);
  const sorted = [...items].sort((a, b) => a.offsetMinutes - b.offsetMinutes);
  // A copy, never the caller's array — `resolveCoverage` sorts for the same
  // reason, and a layout pass that reordered the rows under a React key would
  // be a rendering bug with no visible cause.
  const sortedCoverage = [...coverage].sort((a, b) => a.offsetMinutes - b.offsetMinutes);

  /**
   * The last minute anything actually reaches — the end of the night, or a
   * later item end. This is what the axis bottom used to be, and it stays the
   * thing an open-ended block is drawn to.
   *
   * An open-ended item contributes only its start, because it is drawn *to*
   * this number: letting it help decide the number is circular, and the fixed
   * point it would settle on is "as long as the longest thing already there."
   */
  const contentEndMinutes = Math.max(
    span ?? 0,
    ...sorted.map((i) =>
      i.resolvedDurationMinutes === null
        ? i.offsetMinutes
        : i.offsetMinutes + i.resolvedDurationMinutes,
    ),
  );

  /**
   * The day in view, midnight to midnight, and nothing about what is scheduled
   * in it.
   *
   * It used to be computed from the content: every item, every coverage edge
   * and the end of the night joined into one list and rounded outward to whole
   * days. That made an ordinary 02:30 finish a 48-hour canvas whose second half
   * was almost entirely empty night, and a single flat lay logged the evening
   * before add a third day the same way — the planner paying for the outlier in
   * scroll distance on every visit. The day is chosen now, with the date
   * selector above the axis, and `dayIndex` is the whole of that choice.
   *
   * Through `dayStartOffset`, like the selector's own labels and bounds: one
   * reading of where a day begins, so the day named above the canvas is the day
   * drawn on it.
   */
  const startOffsetMinutes = dayStartOffset(startTime, dayIndex);
  const endOffsetMinutes = startOffsetMinutes + DAY_MINUTES;

  const scale: TimelineScale = { startOffsetMinutes };
  /** The day's own last pixel, which is both its floor and its full height. */
  const dayBottom = topForOffset(scale, endOffsetMinutes);

  /**
   * The items that are on this day, with the minutes each actually runs.
   *
   * Filtered rather than laid out and left off screen: a block on another date
   * has a top outside the canvas, which the scroller would clip — and a clipped
   * block is still in the lane arithmetic, splitting the day into columns to
   * make room for something nobody can see. `contentEndMinutes` above is
   * computed over *every* item and deliberately not over these, because it is
   * the number an open-ended block is drawn to and a block's length must not
   * depend on which day the planner is looking at.
   */
  const spans = sorted
    .map((item) => {
      const start = item.offsetMinutes;
      const end =
        item.resolvedDurationMinutes === null
          ? Math.max(contentEndMinutes, start)
          : start + item.resolvedDurationMinutes;
      return { item, start, end };
    })
    .filter((sp) => onDay(sp.start, sp.end, startOffsetMinutes, endOffsetMinutes));

  // One rule, used twice — see `assignLanes`.
  const itemLanes = assignLanes(spans.map((sp) => ({ start: sp.start, end: sp.end })));

  const blocks: TimelineBlock<T>[] = spans.map((sp, i) => ({
    item: sp.item,
    ...clippedBox(scale, sp.start, sp.end, dayBottom),
    lane: itemLanes[i]!.lane,
    laneCount: itemLanes[i]!.laneCount,
    // The item's *true* length, never the clipped one: this is what `edgeDrag`
    // is handed, and the drags are differential, so a bottom handle sitting on
    // the day's edge rather than on the item's real end still re-lengths the
    // item by the distance the pointer moved. Clipping it would make dragging
    // the one reachable edge of an overrunning block re-length it to the day.
    effectiveDurationMinutes: sp.end - sp.start,
  }));

  /**
   * The capsules in the rail.
   *
   * Built from nothing the blocks produced: a window's geometry is the scale
   * and the window's own two numbers, and nothing about what is scheduled
   * inside it. What it does share with the blocks is `assignLanes` — a rail
   * holding a photographer's whole day and a florist's afternoon has to put
   * them side by side, which is the same question the lane area answers for two
   * events at once, and the same answer.
   *
   * This is where the rail differs from the bands it replaced. A band was drawn
   * across the whole lane area and told apart from its neighbours by a left
   * inset, which is a nudge rather than a layout: the fourth overlapping window
   * had to share the third's inset because the insets were walking off the
   * card. Lanes divide a fixed rail instead, so any number of windows fit by
   * construction and none of them reaches into the day.
   */
  const coverageSpans = sortedCoverage
    .map((annotation) => ({
      annotation,
      start: annotation.offsetMinutes,
      end: annotation.offsetMinutes + annotation.durationMinutes,
    }))
    // On this day, by the same rule the blocks are: a photographer's nine hours
    // can open the evening before and close after midnight, and each day it
    // runs through draws the part of it that is on that day.
    .filter((sp) => onDay(sp.start, sp.end, startOffsetMinutes, endOffsetMinutes));
  const coverageLanes = assignLanes(coverageSpans);

  const coverageBands: TimelineCoverageBand<C>[] = coverageSpans.map((sp, i) => ({
    annotation: sp.annotation,
    // Through `clippedBox` — and so through `topForOffset` — like the blocks and
    // the gutter and a drag, and never a local multiplication: a capsule that
    // worked out its own pixels would sit a pixel off the hour its own gutter
    // prints beside it.
    ...clippedBox(scale, sp.start, sp.end, dayBottom),
    lane: coverageLanes[i]!.lane,
    laneCount: coverageLanes[i]!.laneCount,
  }));

  /**
   * Exactly the day.
   *
   * It used to be the taller of the day and the lowest block, so that the floor
   * under a zero-length block could not push a box off the bottom of the
   * canvas. One day at a time cannot afford that: a canvas taller than its own
   * date is scrollable into a stretch of the next one with no hour labels and
   * no name above it, which is the growth this axis exists to be rid of. A few
   * pixels of overhang are clipped by the scroller instead, which is the honest
   * reading — the block runs off the end of the day, and it does.
   */
  const totalHeight = dayBottom;

  return {
    blocks,
    coverageBands,
    startOffsetMinutes,
    endOffsetMinutes,
    totalHeight,
    /**
     * The end-of-night line, when it is on this day.
     *
     * Inclusive at both edges, so a night finishing at exactly midnight draws
     * on the foot of the wedding day and on the head of the morning after.
     * Both readings are true and the planner may be looking at either.
     */
    endOfNightTop:
      span === null || span < startOffsetMinutes || span > endOffsetMinutes
        ? null
        : topForOffset(scale, span),
  };
}

export interface SnapOptions {
  /** Per-minute instead of per-five — the modifier held during a drag. */
  fine?: boolean;
}

/** The nearest snap step, or the nearest minute when held fine. */
export function snapMinutes(raw: number, { fine = false }: SnapOptions = {}): number {
  const step = fine ? 1 : SNAP_MINUTES;
  return Math.round(raw / step) * step;
}

/**
 * Where a body drag puts an item.
 *
 * The *result* is snapped, not the distance moved: an item sitting at 6:17
 * nudged downward lands on 6:20, which is what makes a dragged day come out in
 * round numbers instead of carrying its old irregularity around forever.
 *
 * Pixels convert to minutes linearly, which is only true when nothing between
 * the grab and the drop is collapsed — which is why a drag crossing a collapsed
 * gap has to expand it first.
 */
export function offsetFromDelta(startOffset: number, dy: number, options?: SnapOptions): number {
  return snapMinutes(startOffset + dy / PX_PER_MINUTE, options);
}

export type DragEdge = 'top' | 'bottom';

/**
 * Where an edge drag puts an item's start and length.
 *
 * The top edge holds the end fixed — offset and duration move by equal and
 * opposite amounts — and the bottom edge holds the start fixed. Anything else
 * and grabbing one edge silently moves the other, which a planner trimming the
 * front of the ceremony would only notice after the fact.
 *
 * Both clamp at one snap step. A zero-height block cannot be grabbed again, and
 * a negative length is not a length at all.
 *
 * Takes the length the block is *drawn* with, not the stored `durationMinutes`:
 * the trailing item stores null so that it tracks the end of the night, and the
 * edge the planner grabbed is the one on screen. `TimelineBlock` carries that
 * number as `effectiveDurationMinutes`.
 */
export function edgeDrag(
  item: { offsetMinutes: number; durationMinutes: number },
  dy: number,
  edge: DragEdge,
  options?: SnapOptions,
): { offsetMinutes: number; durationMinutes: number } {
  const end = item.offsetMinutes + item.durationMinutes;

  if (edge === 'top') {
    const offsetMinutes = Math.min(
      snapMinutes(item.offsetMinutes + dy / PX_PER_MINUTE, options),
      end - SNAP_MINUTES,
    );
    // Measured back from the end, so the clamp cannot move it either.
    return { offsetMinutes, durationMinutes: end - offsetMinutes };
  }

  const draggedEnd = snapMinutes(end + dy / PX_PER_MINUTE, options);
  return {
    offsetMinutes: item.offsetMinutes,
    durationMinutes: Math.max(SNAP_MINUTES, draggedEnd - item.offsetMinutes),
  };
}
